import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import { Box } from "@shopify/polaris";
import { useEffect } from "react";
import Stack from "../../Stack";
import styledComponents from "styled-components";
import paletteColors from "../../../theme/paletteColors";

const Text = styledComponents.p`
  font-size: 13px;
  font-weight: 450;
  font-family: "Inter";
`;
const LabelText = styledComponents(Text)`
  color: ${({ $color }) => $color};
  font-weight: 550;
`;

export const Switch = ({
  checked,
  name,
  label,
  ariaLabel,
  disabled,
  onChange,
  shouldChange,
  activeColor = "#000",
  inactiveColor = "#E5E5E5",
  rounded = false,
  showLabelColon = true,
}) => {
  useEffect(() => {
    const hiddenInput = document.getElementById(`${name}-hidden`);
    if (hiddenInput) {
      hiddenInput.checked = checked;
    }
  }, [checked, name]);

  const labelContent = label ? (
    <LabelText $color={paletteColors.light.dark}>{`${label}${showLabelColon ? ":" : ""}`}</LabelText>
  ) : null;
  const switchControl = (
    <Box
      as="button"
      type="button"
      name={name}
      aria-label={ariaLabel || name}
      aria-pressed={Boolean(checked)}
      onClick={() => {
        const newValue = !checked;
        if (shouldChange && !shouldChange(newValue)) {
          return;
        }
        if (onChange) {
          onChange(newValue);
        }
        const hiddenInput = document.getElementById(`${name}-hidden`);
        if (hiddenInput) {
          hiddenInput.checked = newValue;
          hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }}
      disabled={disabled}
      style={{
        width: "40px",
        height: "24px",
        borderRadius: rounded ? "999px" : "4px",
        backgroundColor: checked ? activeColor : inactiveColor,
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        padding: "2px",
        transition: "background 0.2s",
      }}
    >
      <Box
        style={{
          width: "16px",
          height: "16px",
          borderRadius: rounded ? "50%" : "4px",
          backgroundColor: "#fff",
          position: "absolute",
          left: checked ? "calc(100% - 20px)" : "4px",
          transition: "left 0.2s",
        }}
      />
    </Box>
  );

  return (
    <Stack alignItems="center" spacing={2} flexWrap="nowrap">
      {labelContent}
      {switchControl}
      <input id={`${name}-hidden`} type="checkbox" defaultChecked={checked} style={{ display: "none" }} />
    </Stack>
  );
};

Switch.propTypes = {
  checked: PropTypes.bool,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  shouldChange: PropTypes.func,
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  rounded: PropTypes.bool,
  showLabelColon: PropTypes.bool,
};

export const SwitchComponent = ({
  field,
  name,
  label,
  disabled,
  onChange,
  shouldChange,
  activeColor,
  inactiveColor,
  rounded,
  showLabelColon,
}) => (
  <Switch
    checked={Boolean(field.value)}
    name={name}
    label={label}
    disabled={disabled}
    onChange={(newValue) => {
      field.onChange(newValue);
      onChange?.(newValue);
    }}
    shouldChange={shouldChange}
    activeColor={activeColor}
    inactiveColor={inactiveColor}
    rounded={rounded}
    showLabelColon={showLabelColon}
  />
);

SwitchComponent.propTypes = {
  field: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  shouldChange: PropTypes.func,
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  rounded: PropTypes.bool,
  showLabelColon: PropTypes.bool,
};

RHFSwitch.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  shouldChange: PropTypes.func,
  activeColor: PropTypes.string,
  inactiveColor: PropTypes.string,
  rounded: PropTypes.bool,
  showLabelColon: PropTypes.bool,
};

export default function RHFSwitch({
  name,
  label,
  disabled = false,
  onChange,
  shouldChange,
  activeColor,
  inactiveColor,
  rounded,
  showLabelColon,
}) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SwitchComponent
          field={field}
          name={name}
          label={label}
          disabled={disabled}
          onChange={onChange}
          shouldChange={shouldChange}
          activeColor={activeColor}
          inactiveColor={inactiveColor}
          rounded={rounded}
          showLabelColon={showLabelColon}
        />
      )}
    />
  );
}
